import { useState } from "react";

const createEmptyBoard = (rows, cols) =>
  Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => ({
      r,
      c,
      mine: false,
      adjacent: 0,
      revealed: false,
      flagged: false
    }))
  );

const createBoard = (rows, cols, mines, safeCell = null) => {
  let bestBoard = null;
  let bestSafeArea = 0;
  let attempts = 0;

  while (attempts < 20) {
    attempts++;

    let board = Array(rows)
      .fill()
      .map(() =>
        Array(cols).fill().map(() => ({
          revealed: false,
          mine: false,
          adjacent: 0,
          flagged: false,
        }))
      );

    // Place mines
    let placed = 0;
    while (placed < mines) {
      let r = Math.floor(Math.random() * rows);
      let c = Math.floor(Math.random() * cols);
      if (!board[r][c].mine && (!safeCell || (r !== safeCell.r || c !== safeCell.c))) {
        board[r][c].mine = true;
        placed++;
      }
    }

    // Calculate adjacent mine counts
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!board[r][c].mine) {
          let count = 0;
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (board[r + dr]?.[c + dc]?.mine) count++;
            }
          }
          board[r][c].adjacent = count;
        }
      }
    }

    // Check safe area size
    if (safeCell) {
      const size = countSafeArea(board, safeCell.r, safeCell.c);
      if (size > bestSafeArea) {
        bestSafeArea = size;
        bestBoard = board;
      }
      if (size >= 5) return board; // Good enough
    } else {
      return board;
    }
  }

  return bestBoard || createBoard(rows, cols, mines); // Fallback
};

const countSafeArea = (board, r, c) => {
  let visited = new Set();
  let queue = [{ r, c }];

  while (queue.length) {
    const { r, c } = queue.pop();
    const key = `${r},${c}`;
    if (visited.has(key)) continue;
    visited.add(key);

    if (board[r]?.[c] && !board[r][c].mine) {
      if (board[r][c].adjacent === 0) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            queue.push({ r: r + dr, c: c + dc });
          }
        }
      }
    }
  }
  return visited.size;
};

export default function MineSweeper() {
  const [rows, setRows] = useState(18);
  const [cols, setCols] = useState(18);
  const [mines, setMines] = useState(40);
  const [board, setBoard] = useState(createBoard(18, 18, 40));
  const [difficulty, setDifficulty] = useState("Medium");
  const [firstClick, setFirstClick] = useState(true);
  const [ cellSize, setCellSize ] = useState(28);

  const difficulties = {
    Easy: { rows: 10, cols: 10, mines: 15, cellSize: 32 },
    Medium: { rows: 18, cols: 18, mines: 40, cellSize: 28 },
    Hard: { rows: 24, cols: 24, mines: 99, cellSize: 22 }
  };

  const resetGame = (r, c, m, cs, label) => {
    setRows(r);
    setCols(c);
    setMines(m);
    setDifficulty(label);
    setFirstClick(true);
    setCellSize(cs);
    setBoard(createBoard(r, c, m));
  };

  const checkWin = (boardCopy) => {
    const safeCells = rows * cols - mines;
    const revealedCells = boardCopy.flat().filter(cell => cell.revealed && !cell.mine).length;
    if (revealedCells === safeCells) {
      alert("🎉 You Win!");
    }
  };

  const floodReveal = (startR, startC, boardCopy) => {
    const queue = [[startR, startC]];
    const visited = new Set();

    while (queue.length > 0) {
      const [r, c] = queue.shift();
      const key = `${r},${c}`;

      if (visited.has(key)) continue;
      visited.add(key);

      if (boardCopy[r][c].revealed || boardCopy[r][c].flagged) continue;

      boardCopy[r][c].revealed = true;

      if (boardCopy[r][c].adjacent === 0) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr;
            const nc = c + dc;
            if (boardCopy[nr]?.[nc]) {
              queue.push([nr, nc]);
            }
          }
        }
      }
    }
  };


  const revealCell = (r, c, newBoard = null, isFirstClick = false) => {
    if (firstClick && !isFirstClick) {
      console.log("First click, generating board...");
      const freshBoard = createBoard(rows, cols, mines, { r, c });
      setBoard(freshBoard);
      setFirstClick(false);
      revealCell(r, c, freshBoard, true);
      return;
    }

    const boardCopy = newBoard || board.map(row => row.map(cell => ({ ...cell })));

    if (boardCopy[r][c].revealed || boardCopy[r][c].flagged) return;

    if (boardCopy[r][c].mine) {
      boardCopy.forEach(row =>
        row.forEach(cell => {
          if (cell.mine) cell.revealed = true;
        })
      );

      alert("💥 Game Over!");
      setBoard(boardCopy);
      return;
    }

    // Use BFS flood fill
    floodReveal(r, c, boardCopy);

    setBoard(boardCopy);
    checkWin(boardCopy);
  };


  const toggleFlag = (e, r, c) => {
    e.preventDefault();
    const boardCopy = board.map(row => row.map(cell => ({ ...cell })));
    if (!boardCopy[r][c].revealed) {
      boardCopy[r][c].flagged = !boardCopy[r][c].flagged;
    }
    setBoard(boardCopy);
  };

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ marginRight: "8px", fontWeight: "bold" }}>Difficulty:</label>
        <select
          value={difficulty}
          onChange={(e) => {
            resetGame(difficulties[e.target.value].rows, difficulties[e.target.value].cols, difficulties[e.target.value].mines, difficulties[e.target.value].cellSize, difficulties[e.target.value].value);
          }}
          style={{
            padding: "6px",
            marginBottom: "10px",
            fontSize: "16px"
          }}
        >
          {Object.keys(difficulties).map(diff => (
            <option key={diff} value={diff}>{diff}</option>
          ))}
        </select>

        <button
          onClick={() => {
            setFirstClick(true);
            setBoard(createEmptyBoard(rows, cols));
          }}
          style={{
            padding: "6px 12px",
            marginLeft: "10px",
            background: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          Reset Game
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          gap: "2px",
          justifyContent: "center",
          margin: "0 auto",
        }}
      >
        {board.map((row, r) =>
          row.map((cell, c) => (
            <div
              key={`${r}-${c}`}
              onClick={() => revealCell(r, c, firstClick)}
              onContextMenu={(e) => toggleFlag(e, r, c)}
              style={{
                width: cellSize,
                height: cellSize,
                background: cell.revealed ? "#ddd" : "#888",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                fontWeight: "bold",
                color: cell.mine ? "red" : "black",
                borderRadius: "4px",
                userSelect: "none",
              }}
            >
              {cell.revealed
                ? cell.mine
                  ? "💣"
                  : cell.adjacent || ""
                : cell.flagged
                ? "🚩"
                : ""}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
